import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { matchesInputAction } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import {
    DOUBLE_CLICK_MS,
    DRAG_THRESHOLD_PX,
    chordFromMouseEvent
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { handleOnInsertionStart } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface PendingCatalogueDrag {
    articleId: string;
    pointerId: number;
    button: number;
    startX: number;
    startY: number;
    started: boolean;
}

interface CompletedCatalogueClick {
    articleId: string;
    button: number;
    timeStamp: number;
}

const buttonMask = (button: number): number => (button === 0 ? 1 : button === 1 ? 4 : 2);

const matchesDragBinding = (event: MouseEvent) =>
    matchesInputAction(InputActionId.ArticleCatalogueDrag, chordFromMouseEvent(event, InputTrigger.Drag));

export const useArticleDragHandler = () => {
    const { t } = useTranslation();
    const pendingDragRef = useRef<PendingCatalogueDrag | null>(null);
    const completedClickRef = useRef<CompletedCatalogueClick | null>(null);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (event.button < 0 || event.button > 2) {
                completedClickRef.current = null;
                return;
            }

            if (
                event
                    .composedPath()
                    .some(
                        (item) =>
                            item instanceof HTMLElement &&
                            (item.matches('button, [role="button"]') || item.hasAttribute('data-article-drag-ignore'))
                    )
            ) {
                completedClickRef.current = null;
                return;
            }

            const target = event
                .composedPath()
                .find(
                    (item): item is HTMLElement =>
                        item instanceof HTMLElement && item.hasAttribute('data-article-drag-id')
                );
            const articleId = target?.dataset.articleDragId;
            if (!articleId || !matchesDragBinding(event)) {
                completedClickRef.current = null;
                return;
            }

            const state = usePlacementStore.getState();
            const previousClick = completedClickRef.current;
            const isConfiguredDoubleClick =
                previousClick !== null &&
                previousClick.articleId === articleId &&
                previousClick.button === event.button &&
                event.timeStamp - previousClick.timeStamp <= DOUBLE_CLICK_MS;

            if (isConfiguredDoubleClick) {
                completedClickRef.current = null;
                pendingDragRef.current = null;
                state.setSelectedArticleId(articleId);
                if (event.button !== 0) {
                    event.preventDefault();
                }
                handleOnInsertionStart(true, articleId, t);
                return;
            }

            pendingDragRef.current = {
                articleId,
                pointerId: event.pointerId,
                button: event.button,
                startX: event.clientX,
                startY: event.clientY,
                started: false
            };
            state.setSelectedArticleId(articleId);
            state.beginManualArticleInsertionDrag(event.clientX, event.clientY, event.button);
            if (event.button !== 0) {
                event.preventDefault();
            }
        };

        const handlePointerMove = (event: PointerEvent) => {
            const pending = pendingDragRef.current;
            if (!pending || pending.pointerId !== event.pointerId || pending.started) {
                return;
            }
            if ((event.buttons & buttonMask(pending.button)) === 0) {
                return;
            }

            const dx = event.clientX - pending.startX;
            const dy = event.clientY - pending.startY;
            if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
                return;
            }

            pending.started = true;
            event.preventDefault();
            handleOnInsertionStart(false, pending.articleId, t);
        };

        const handlePointerUp = (event: PointerEvent) => {
            const pending = pendingDragRef.current;
            if (!pending || pending.pointerId !== event.pointerId || pending.button !== event.button) {
                return;
            }
            pendingDragRef.current = null;
            if (!pending.started) {
                completedClickRef.current = {
                    articleId: pending.articleId,
                    button: pending.button,
                    timeStamp: event.timeStamp
                };
                usePlacementStore.getState().cancelManualArticleInsertionDrag();
            } else {
                completedClickRef.current = null;
            }
        };

        const handlePointerCancel = (event: PointerEvent) => {
            const pending = pendingDragRef.current;
            if (!pending || pending.pointerId !== event.pointerId) {
                return;
            }
            pendingDragRef.current = null;
            completedClickRef.current = null;
            const state = usePlacementStore.getState();
            if (pending.started && (state.isDragNewArticleActiveRef.current || state.isDragActiveRef.current)) {
                state.handleOnDragArticleEnd(true);
            } else {
                state.cancelManualArticleInsertionDrag();
            }
        };

        const handleContextMenu = (event: MouseEvent) => {
            const isCatalogueRow =
                event.target instanceof HTMLElement && event.target.closest('[data-article-drag-id]') !== null;
            if (isCatalogueRow && matchesDragBinding(event)) {
                event.preventDefault();
            }
        };

        window.addEventListener('pointerdown', handlePointerDown, true);
        window.addEventListener('pointermove', handlePointerMove, true);
        window.addEventListener('pointerup', handlePointerUp, true);
        window.addEventListener('pointercancel', handlePointerCancel, true);
        window.addEventListener('contextmenu', handleContextMenu, true);

        return () => {
            window.removeEventListener('pointerdown', handlePointerDown, true);
            window.removeEventListener('pointermove', handlePointerMove, true);
            window.removeEventListener('pointerup', handlePointerUp, true);
            window.removeEventListener('pointercancel', handlePointerCancel, true);
            window.removeEventListener('contextmenu', handleContextMenu, true);
        };
    }, [t]);
};
